import React, { useRef, useEffect } from 'react';

/**
 * 6-box OTP input with auto-advance, backspace, paste support.
 * Emits the raw string via onChange (up to 6 chars).
 */
export default function OtpInput({ value = '', onChange, autoFocus = true, disabled = false }) {
  const refs = useRef([]);
  const digits = (value + '      ').slice(0, 6).split('');

  useEffect(() => {
    if (autoFocus && refs.current[0]) refs.current[0].focus();
  }, [autoFocus]);

  const setDigit = (i, ch) => {
    const clean = (ch || '').replace(/\D/g, '').slice(0, 1);
    const next = value.split('');
    while (next.length < 6) next.push('');
    next[i] = clean;
    const joined = next.join('').trimEnd();
    onChange(joined);
    if (clean && i < 5) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace') {
      if (!digits[i].trim() && i > 0) {
        e.preventDefault();
        refs.current[i - 1]?.focus();
        setDigit(i - 1, '');
      } else {
        setDigit(i, '');
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < 5) {
      refs.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (pasted) {
      e.preventDefault();
      onChange(pasted);
      refs.current[Math.min(pasted.length, 5)]?.focus();
    }
  };

  return (
    <div className="flex gap-2 justify-center" data-testid="otp-input">
      {[0, 1, 2, 3, 4, 5].map(i => (
        <input
          key={i}
          ref={el => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digits[i].trim()}
          onChange={e => setDigit(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className="w-12 h-14 text-center text-2xl font-mono font-semibold rounded-lg outline-none transition-all disabled:opacity-50"
          style={{
            background: '#0D0F14',
            border: `1px solid ${digits[i].trim() ? '#F5A623' : '#1E2028'}`,
            color: '#FFFFFF',
          }}
          data-testid={`otp-digit-${i}`}
          aria-label={`Digit ${i + 1} of 6`}
        />
      ))}
    </div>
  );
}
